import type { JsonValue } from "@e308/core";
import { microFromString } from "./exact-numbers.js";
import { cloneLogisticsState } from "./logistics-clone.js";
import {
  EMPTY_TROOPS,
  normalizeTroops,
  requiredBorder,
  subtractTroops,
  travelDeadline,
  troopQuantity,
} from "./logistics-math.js";
import type {
  AutoReinforceOrder,
  LogisticsState,
  StandingOrder,
  TransitOrder,
  TroopComposition,
} from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { exactRecordKeys as exactKeys } from "./record-validation.js";

export function emptyTroops(): TroopComposition {
  return EMPTY_TROOPS;
}

export function initialLogisticsState(borderIds: readonly string[]): LogisticsState & JsonValue {
  return cloneLogisticsState({
    nextTransitId: 1,
    nextAutoOrderId: 1,
    nextAutoReinforceAtMs: 5_000,
    borders: Object.fromEntries(
      borderIds.map((id) => [id, { troops: EMPTY_TROOPS, standingOrder: "hold" as const }]),
    ),
    transits: [],
    autoReinforce: [],
  }) as LogisticsState & JsonValue;
}

export function startTravel(
  state: LogisticsState,
  origin: "home" | string,
  destination: "home" | string,
  units: TroopComposition,
  nowMs: number,
  travelMultiplier: string,
  stepMs: number,
): LogisticsState & JsonValue {
  validateMove(state, origin, destination, units);
  const borders = { ...state.borders };
  if (origin !== "home") {
    const position = requiredBorder(state, origin);
    borders[origin] = { ...position, troops: subtractTroops(position.troops, units) };
  }
  const firstDestination = origin === "home" ? destination : "home";
  const finalDestination = origin !== "home" && destination !== "home" ? destination : null;
  const transit: TransitOrder = {
    id: `transit-${state.nextTransitId}`,
    units: normalizeTroops(units),
    destination: firstDestination,
    finalDestination,
    completesAtMs: travelDeadline(nowMs, travelMultiplier, stepMs),
    returnAfterArrival: false,
  };
  return cloneLogisticsState({
    ...state,
    nextTransitId: state.nextTransitId + 1,
    borders,
    transits: [...state.transits, transit],
  }) as LogisticsState & JsonValue;
}

export function cancelTransit(state: LogisticsState, orderId: string): LogisticsState & JsonValue {
  const transit = state.transits.find((entry) => entry.id === orderId);
  if (!transit) throw new TypeError(`Unknown transit order: ${orderId}`);
  if (transit.destination === "home") {
    return cloneLogisticsState({
      ...state,
      transits: state.transits.map((entry) =>
        entry.id === orderId ? { ...entry, finalDestination: null } : entry,
      ),
    }) as LogisticsState & JsonValue;
  }
  return cloneLogisticsState({
    ...state,
    transits: state.transits.map((entry) =>
      entry.id === orderId ? { ...entry, finalDestination: null, returnAfterArrival: true } : entry,
    ),
  }) as LogisticsState & JsonValue;
}

export function setStandingOrder(
  state: LogisticsState,
  borderId: string,
  order: StandingOrder,
): LogisticsState & JsonValue {
  const position = requiredBorder(state, borderId);
  return cloneLogisticsState({
    ...state,
    borders: { ...state.borders, [borderId]: { ...position, standingOrder: order } },
  }) as LogisticsState & JsonValue;
}

export function enqueueAutoReinforce(
  state: LogisticsState,
  destination: string,
  desired: Readonly<Record<string, string | null>>,
): LogisticsState & JsonValue {
  if (state.reinforcement) throw new TypeError("Count targets are retired; use production routing");
  requiredBorder(state, destination);
  exactKeys(desired, UNIT_IDS);
  const normalized = Object.fromEntries(
    UNIT_IDS.map((id) => [
      id,
      desired[id] === null ? null : troopQuantity(desired[id], "desired count"),
    ]),
  ) as AutoReinforceOrder["desired"];
  const order: AutoReinforceOrder = {
    id: `auto-${state.nextAutoOrderId}`,
    destination,
    desired: normalized,
  };
  return cloneLogisticsState({
    ...state,
    nextAutoOrderId: state.nextAutoOrderId + 1,
    autoReinforce: [...state.autoReinforce, order],
  }) as LogisticsState & JsonValue;
}

export function cancelAutoReinforce(
  state: LogisticsState,
  orderId: string,
): LogisticsState & JsonValue {
  if (!state.autoReinforce.some((order) => order.id === orderId))
    throw new TypeError(`Unknown auto-reinforce order: ${orderId}`);
  return cloneLogisticsState({
    ...state,
    autoReinforce: state.autoReinforce.filter((order) => order.id !== orderId),
  }) as LogisticsState & JsonValue;
}

export function reorderAutoReinforce(
  state: LogisticsState,
  orderIds: readonly string[],
): LogisticsState & JsonValue {
  if (
    orderIds.length !== state.autoReinforce.length ||
    new Set(orderIds).size !== orderIds.length ||
    orderIds.some((id) => !state.autoReinforce.some((order) => order.id === id))
  )
    throw new TypeError("Auto-reinforce reorder must contain every order exactly once");
  return cloneLogisticsState({
    ...state,
    autoReinforce: orderIds.map(
      (id) => state.autoReinforce.find((order) => order.id === id) as AutoReinforceOrder,
    ),
  }) as LogisticsState & JsonValue;
}

function validateMove(
  state: LogisticsState,
  origin: string,
  destination: string,
  units: TroopComposition,
): void {
  if (origin === destination) throw new TypeError("Travel origin and destination must differ");
  if (origin !== "home") requiredBorder(state, origin);
  if (destination !== "home") requiredBorder(state, destination);
  normalizeTroops(units);
  if (UNIT_IDS.every((id) => microFromString(units[id]) === 0n))
    throw new TypeError("Travel requires troops");
  if (origin !== "home") subtractTroops(requiredBorder(state, origin).troops, units);
}

export { addTroops, subtractTroops, supplyForPosition, travelDeadline } from "./logistics-math.js";
export { validateLogisticsState } from "./logistics-validation.js";
